---
title: "Top Candlestick Patterns and Their Context"
slug: candlestick-patterns
shortTitle: "Candlestick Patterns"
description: "The candlestick patterns traders watch most, from doji and hammers to engulfing candles and morning stars, and the context that decides whether they matter."
level: intermediate
category: trading
course: intermediate
order: 6
topics: [chart-patterns, technical-analysis]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/candlestick-patterns.svg
faqs:
  - q: "Which candlestick pattern is the most reliable?"
    a: "No single pattern is reliable on its own. Engulfing candles, hammers and shooting stars are widely watched, but their usefulness depends far more on where they form, the trend and the timeframe than on the shape itself."
  - q: "Should I trade a pattern before the candle closes?"
    a: "Most traders wait. Until the candle closes, its body and wicks can still change completely, so a hammer halfway through the hour may end as an ordinary down candle. The candle-close countdown in the chart settings shows how long is left."
  - q: "Do candlestick patterns work on Heikin-Ashi charts?"
    a: "Not in the usual way. Heikin-Ashi candles are built from averaged prices, so their opens, closes and wicks differ from the real ones. Switch to regular or hollow candles when you look for classic patterns."
  - q: "What timeframe is best for candlestick patterns?"
    a: "Patterns on higher timeframes, such as 4-hour and daily candles, summarize more trading and tend to carry more weight than patterns on 1-minute or 5-minute charts, which are full of noise. Whatever the timeframe, the pattern still needs a sensible location."
  - q: "Does the platform detect candlestick patterns for me?"
    a: "No. The chart shows the candles and their open, high, low and close, and you judge the patterns yourself. That is no bad thing, because the context that decides whether a pattern matters has to be judged by you anyway."
---

Candlestick patterns are short sequences of one to three candles that traders have named because they often appear around turning points or pauses. A long lower wick, a big candle swallowing the previous one, a tiny body between two large ones: each tells a small story about who controlled price during that period and whether that control is changing.

The shapes are easy to memorize. The harder skill, and the one this lesson focuses on, is **context**: where the pattern forms, what the trend is doing and whether the next candles confirm it. If you need a refresher on bodies, wicks and how a candle is built, start with [Reading a Candlestick Chart for the First Time](/academy/article/reading-candlestick-charts).

<CTA />

<KeyTakeaways>

- **Patterns describe a shift in control.** Long wicks, engulfing bodies and star formations show buyers or sellers being pushed back within a period.
- **Location matters most.** A hammer at a well-tested support level means far more than the same shape in the middle of a range.
- **Wait for the close and confirmation.** A pattern is not finished until its candle closes, and many traders also want the next candle to follow through.
- **Patterns fail often.** Use them to time an entry with a defined stop, never as a reason on their own to trade.

</KeyTakeaways>

## Single-candle patterns

**Doji.** The open and close are almost equal, leaving a very thin body. It shows indecision: buyers and sellers ended the period roughly where they started. After a long trend, a doji can be an early sign that momentum is pausing.

**Hammer and hanging man.** A small body near the top of the range with a long lower wick, at least twice the body, and little or no upper wick. After a decline it is called a **hammer**: sellers pushed price down, then buyers drove it back up. The same shape after a rise is a **hanging man**, a warning that selling pressure appeared during the period.

**Shooting star and inverted hammer.** The mirror image: a small body near the bottom with a long upper wick. After a rise it is a **shooting star**, showing buyers were rejected at higher prices. After a fall it is an **inverted hammer**.

**Marubozu.** A large body with little or no wick. One side controlled the whole period, which often signals strength in the direction of the candle rather than a reversal.

<Example title="Measuring a hammer on ETH/USDT (illustrative prices)">

A daily candle opens at 3,010, trades down to 2,940, up to 3,020 and closes at 3,015.

- Body: 3,015 − 3,010 = 5
- Lower wick: 3,010 − 2,940 = 70, which is 14 times the body
- Upper wick: 3,020 − 3,015 = 5
- Close position: (3,015 − 2,940) ÷ (3,020 − 2,940) = 75 ÷ 80 = **94%** of the way up the range

It qualifies as a hammer shape. Whether it matters depends on where it formed.

</Example>

## Two-candle patterns

**Engulfing.** A **bullish engulfing** pattern is a down candle followed by an up candle whose body completely covers the first body. A **bearish engulfing** pattern is the reverse. It shows one side overwhelming the other within a single period.

**Harami (inside bar).** A large candle followed by a small one whose body sits inside the first body. It signals a pause or loss of momentum after a strong move, and traders often watch for a break of the larger candle's high or low.

**Tweezer tops and bottoms.** Two candles with matching highs (top) or matching lows (bottom), showing a price that was rejected twice in a row.

## Three-candle patterns

**Morning star and evening star.** A **morning star** starts with a strong down candle, then a small-bodied candle (often a doji), then a strong up candle closing well into the first candle's body. It describes selling fading and buying taking over. The **evening star** is the bearish mirror image at the top of a rise.

**Three white soldiers and three black crows.** Three consecutive strong candles in the same direction, each closing near its extreme. They show sustained pressure, and often appear at the start of a move away from a base.

<CompareTable caption="Common patterns and where they matter most">

| Pattern | Candles | What it shows | Most meaningful when |
| --- | --- | --- | --- |
| Doji | 1 | Indecision | After an extended move, at a level |
| Hammer / shooting star | 1 | Rejection of lower / higher prices | At support / at resistance |
| Engulfing | 2 | One side overwhelming the other | At a level, against a stretched move |
| Harami (inside bar) | 2 | Pause after a strong candle | Before a break of the large candle |
| Morning / evening star | 3 | Gradual change of control | After a clear trend, at a level |
| Three soldiers / crows | 3 | Sustained pressure | Breaking away from a base or range |

</CompareTable>

## Context decides whether a pattern matters

The same shape can mean very different things depending on its surroundings. Before you act, run through these questions:

- **Where is it?** Patterns at well-tested support or resistance, covered in [Support and Resistance](/academy/article/support-and-resistance), are worth far more than those in the middle of a range.
- **What is the trend?** A bullish pattern on a pullback within an uptrend lines up with the bigger picture. The same pattern against a strong downtrend is fighting it.
- **What timeframe?** A daily hammer summarizes a full day of trading. A 1-minute hammer summarizes sixty seconds and is mostly noise.
- **Is there volume behind it?** An engulfing candle on unusually high volume shows more participation than one on a quiet session.
- **Did the next candle confirm it?** Many traders wait for the following candle to trade beyond the pattern's high (for a bullish pattern) or low (for a bearish one).

<Warning>

Patterns are tendencies, not rules, and they fail often. A hammer at support can be followed by a clean break lower. Always define where the pattern is proven wrong, which is usually beyond its wick, place your stop there, and size the trade so that a loss at the stop stays within your limit. On stock and index CFDs, prices can also gap past a stop when the market reopens.

</Warning>

## Turning a pattern into a trade plan

<Example title="A hammer at support (illustrative prices)">

The ETH hammer above formed at a support zone that has held twice before, and a previous swing high sits at 3,205.

- Entry: a **Buy Stop** at 3,025, just above the hammer's high, so you only enter if price follows through
- Stop loss: 2,935, just below the wick, a distance of 3,025 − 2,935 = 90
- Risk budget: 45 USDT, so size = 45 ÷ 90 = **0.5 ETH**
- Target: 3,205, a distance of 180, giving a reward-to-risk of 180 ÷ 90 = **2 to 1**

</Example>

On {{BRAND}}, you would place this on the **Limit / Stop** tab: a buy price above the Ask becomes a Buy Stop. Switch the size to **Lots** and check the **Contract size** row to convert 0.5 ETH into lots. [Position Sizing](/academy/article/position-sizing) walks through that conversion step by step.

{/* TODO: verify with platform — ETH contract size, if the example should quote 0.5 ETH in lots. */}

## Reading candles on {{BRAND}}

The trade chart offers the timeframes **1m, 5m, 15m, 1h, 4h and 1D**, and several chart types. For pattern work, use **candles** or **hollow candles**. **Heikin-Ashi** candles are built from averaged prices, so they smooth trends nicely but change the opens, closes and wicks that classic patterns depend on. The platform does not detect patterns for you; you read them yourself.

<ProTip>

Turn on the **Candle-close countdown** and the **OHLC legend** in the chart settings. The countdown stops you acting on a pattern that is still forming, and the legend gives you exact open, high, low and close values to measure bodies and wicks.

</ProTip>

## Conclusion

Candlestick patterns are short stories about control: rejection wicks, engulfing bodies and star formations show one side gaining ground on the other. Learn the shapes, but judge them by their context: the level they form at, the trend around them, the timeframe and whether the next candle confirms them. Wait for the candle to close, put your stop beyond the pattern, size the trade from that distance, and accept that many patterns will fail. Used that way, they become a useful timing tool rather than a guess. [Chart Patterns](/academy/article/chart-patterns) takes the same ideas to larger formations.
